import { Injectable, computed, signal } from '@angular/core';
import { ActivityEvent, ActivityFilter } from '@pages/activity/activity.types';
import { loadActivityState, saveActivityState } from '@pages/activity/activity.storage';
import { ActivityApiService } from '@core/services/activity-api.service';

@Injectable({ providedIn: 'root' })
export class ActivityStore {
  private readonly _events = signal<ActivityEvent[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<string | null>(null);

  private readonly _filter = signal<ActivityFilter>('all');

  // local persistence
  private readonly _readIds = signal<Set<string>>(new Set(loadActivityState().readIds));
  private readonly _lastSeenAt = signal<string | undefined>(loadActivityState().lastSeenAt);

  constructor(private readonly api: ActivityApiService) {}

  // public
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly filter = this._filter.asReadonly();
  readonly lastSeenAt = this._lastSeenAt.asReadonly();

  setFilter(f: ActivityFilter) {
    this._filter.set(f);
  }

  // Merge read state into server events
  private mergeRead(server: ActivityEvent[]): ActivityEvent[] {
    const read = this._readIds();
    return server.map(e => ({ ...e, isRead: read.has(e.id) }));
  }

  async refresh(): Promise<void> {
    try {
      this._loading.set(true);
      this._error.set(null);

      const server = await this.api.fetchActivity();
      const merged = this.mergeRead(server).sort((a, b) => b.createdAt.localeCompare(a.createdAt));

      this._events.set(merged);

      // update last seen to now
      this._lastSeenAt.set(new Date().toISOString());
      this.persist();
    } catch {
      this._error.set('Could not load activity right now.');
    } finally {
      this._loading.set(false);
    }
  }

  // derived
  readonly filtered = computed(() => {
    const f = this._filter();
    const list = this._events();

    const matches = (e: ActivityEvent): boolean => {
      if (f === 'all') return true;
      if (f === 'replies') return e.type === 'reply_created' || e.type === 'comment_created';
      if (f === 'moderation')
        return (
          e.type.includes('removed') ||
          e.type === 'post_under_review' ||
          e.type === 'account_warning' ||
          e.type === 'post_removed_misinformation'
        );
      if (f === 'follows') return e.type === 'new_activity_on_followed_post';
      if (f === 'system') return e.type === 'welcome_tip';
      return true;
    };

    return list.filter(matches);
  });

  readonly unread = computed(() => this.filtered().filter(x => !x.isRead));
  readonly earlier = computed(() => this.filtered().filter(x => x.isRead));
  readonly unreadCount = computed(() => this._events().filter(x => !x.isRead).length);

  markRead(id: string) {
    const s = new Set(this._readIds());
    s.add(id);
    this._readIds.set(s);

    this._events.set(this._events().map(e => (e.id === id ? { ...e, isRead: true } : e)));
    this.persist();
  }

  markAllRead() {
    const ids = this._events().map(e => e.id);
    const s = new Set(this._readIds());
    ids.forEach(x => s.add(x));
    this._readIds.set(s);

    this._events.set(this._events().map(e => ({ ...e, isRead: true })));
    this.persist();
  }

  private persist() {
    saveActivityState({
      readIds: Array.from(this._readIds()),
      lastSeenAt: this._lastSeenAt(),
    });
  }
}
